<template>
  <div>
    <h3 class="subtitle-1 font-weight-bold mb-3">
      {{ $t('admin.productsDatabase.importWizard.upload.title') }}
    </h3>
    
    <p
      v-if="isEditing"
      class="mb-4"
    >
      {{ $t('admin.productsDatabase.importWizard.upload.editingDescription') }}
    </p>
    <p
      v-else
      class="mb-4"
    >
      {{ $t('admin.productsDatabase.importWizard.upload.description') }}
    </p>

    <!-- Physical Product Checkbox -->
    <v-checkbox
      v-if="!isEditing"
      v-model="isPhysicalProduct"
      :label="$t('admin.productsDatabase.importWizard.upload.physicalProduct')"
      class="mb-4"
    />

    <!-- Physical Product Title Input -->
    <v-text-field
      v-if="!isEditing && isPhysicalProduct"
      v-model="physicalProductTitle"
      :label="$t('admin.productsDatabase.importWizard.upload.physicalProductTitle')"
      :rules="physicalProductTitleRules"
      outlined
      dense
      class="mb-4"
    />

    <!-- Binding Type Selection -->
    <v-radio-group
      v-if="isPhysicalProduct"
      v-model="bindingType"
      row
      class="mb-4 mt-0"
    >
      <template #label>
        <div class="text-subtitle-1 mb-1">
          {{ $t('admin.productsDatabase.importWizard.upload.bindingType') }}
        </div>
      </template>
      <v-radio
        value="paperback"
        :label="$t('admin.productsDatabase.importWizard.upload.paperback')"
      />
      <v-radio
        value="hardcover"
        :label="$t('admin.productsDatabase.importWizard.upload.hardcover')"
      />
    </v-radio-group>

    <!-- Physical Product Actions -->
    <div
      v-if="!isEditing && isPhysicalProduct"
      class="mb-4"
    >
      <v-btn
        color="primary"
        :disabled="!isPhysicalProductValid"
        :loading="creatingPhysicalProduct"
        @click="createPhysicalProduct"
      >
        {{ $t('admin.productsDatabase.importWizard.upload.createPhysicalProduct') }}
      </v-btn>
    </div>

    <FileUpload
      v-if="!isEditing && !isPhysicalProduct" 
      :upload-url="uploadUrl"
      :accept-types="acceptedExtensions.join(',')"
      file-types="EPUB, PDF, and LPF files"
      :max-file-size="maxFileSize"
      :validate-file="validateBookFile"
      :include-auth-token="true"
      :show-upload-button="false"
      :drag-drop-prompt="$t('admin.productsDatabase.importWizard.upload.dragDrop')"
      :uploading-message="$t('admin.productsDatabase.importWizard.upload.uploading')"
      :success-message="$t('admin.productsDatabase.importWizard.upload.success')"
      :browse-files-text="$t('admin.productsDatabase.importWizard.upload.browseFiles')"
      :upload-button-text="$t('admin.productsDatabase.importWizard.upload.uploadFile')"
      @file-selected="handleFileSelected"
      @upload-success="handleUploadSuccess"
      @upload-error="handleUploadError"
      @upload-progress="handleUploadProgress"
      @reset="resetUpload"
    />

    <div
      v-if="shouldShowDetails"
      class="mt-4"
    >
      <v-alert
        v-if="uploadedProductInfo"
        :color="uploadStatus.color"
        dense
        outlined
        class="mb-4"
      >
        <div class="font-weight-bold">
          {{ uploadStatus.title }}
        </div>
        <div>{{ uploadStatus.message }}</div>
      </v-alert>
      
      <div class="mb-4">
        <h3 class="subtitle-1 font-weight-bold mb-3">
          {{ $t('admin.productsDatabase.importWizard.upload.systemDetails') }}
        </h3>
        <v-simple-table
          dense
          outlined
        >
          <tbody>
            <tr v-if="productDisplayData.bookId">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.bookId') }}
              </td>
              <td>{{ productDisplayData.bookId }}</td>
            </tr>
            <tr v-if="productDisplayData.mediaObjectId">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.mediaObjectId') }}
              </td>
              <td>{{ productDisplayData.mediaObjectId }}</td>
            </tr>
            <tr v-if="productDisplayData.mediaObjectFilePath">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.filePath') }}
              </td>
              <td>{{ productDisplayData.mediaObjectFilePath }}</td>
            </tr>
            <tr v-if="productDisplayData.title">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.bookTitle') }}
              </td>
              <td>{{ productDisplayData.title }}</td>
            </tr>
            <tr v-if="productDisplayData.encryptedFilePath">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.encryptedFilePath') }}
              </td>
              <td>{{ productDisplayData.encryptedFilePath }}</td>
            </tr>
            <tr v-if="productDisplayData.unencryptedFilePath">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.unencryptedFilePath') }}
              </td>
              <td>{{ productDisplayData.unencryptedFilePath }}</td>
            </tr>
            <tr v-if="productDisplayData.format">
              <td class="font-weight-medium">
                {{ $t('admin.productsDatabase.importWizard.upload.format') }}
              </td>
              <td>{{ productDisplayData.format }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
      
      <div
        v-if="uploadedProductInfo && uploadedProductInfo.encryption"
        class="mb-5"
      >
        <h3 class="subtitle-1 font-weight-bold mb-3">
          {{ $t('admin.productsDatabase.importWizard.upload.encryptionDetails') }}
        </h3>
        <v-simple-table dense>
          <template #default>
            <tbody>
              <tr>
                <td class="font-weight-medium">
                  {{ $t('admin.productsDatabase.importWizard.upload.contentId') }}
                </td>
                <td>{{ uploadedProductInfo.encryption.contentId }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  {{ $t('admin.productsDatabase.importWizard.upload.originalFilename') }}
                </td>
                <td>{{ uploadedProductInfo.encryption.originalFilename }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  {{ $t('admin.productsDatabase.importWizard.upload.encryptedFilename') }}
                </td>
                <td>{{ uploadedProductInfo.encryption.encryptedFilename }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  {{ $t('admin.productsDatabase.importWizard.upload.encryptedFilePath') }}
                </td>
                <td>{{ uploadedProductInfo.encryption.encryptedFilePath }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">
                  {{ $t('admin.productsDatabase.importWizard.upload.status') }}
                </td>
                <td>
                  <v-chip
                    :color="uploadedProductInfo.encryption.status === 'success' ? 'success' : 'error'"
                    x-small
                    text-color="white"
                  >
                    {{ uploadedProductInfo.encryption.status }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      
      <ekitabu-expansion-panel
        v-if="uploadedProductInfo && hasDetailedOutput"
        :title="$t('admin.productsDatabase.importWizard.upload.technicalDetails')"
      >
        <div>
          <div v-if="uploadedProductInfo && uploadedProductInfo.encryption && uploadedProductInfo.encryption.output">
            <h3 class="subtitle-1 font-weight-bold mb-2">
              {{ $t('admin.productsDatabase.importWizard.upload.rawOutput') }}
            </h3>
            <v-card outlined>
              <v-card-text class="pa-2">
                <pre class="output-pre">{{ uploadedProductInfo.encryption.output }}</pre>
              </v-card-text>
            </v-card>
          </div>
            
          <div class="mt-4">
            <h3 class="subtitle-1 font-weight-bold mb-2">
              {{ $t('admin.productsDatabase.importWizard.upload.completeResponse') }}
            </h3>
            <v-card outlined>
              <v-card-text class="pa-2">
                <pre class="output-pre">{{ JSON.stringify(uploadedProductInfo, null, 2) }}</pre>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </ekitabu-expansion-panel>
    </div>
  </div>
</template>

<script>
import FileUpload from '@/components/FileUpload.vue';
import physicalProductMixin from '@/mixins/physicalProductMixin';
import { FILE_SIZE_LIMITS } from '@/utils/Const';
import { isPrintFormat } from '@/utils/BookFormatHelper';

// Get API base URL from the environment or use a default
const apiBaseUrl = process.env.VUE_APP_API_HOST || 'https://localhost';

export default {
  name: 'ProductUpload',
  components: {
    FileUpload
  },
  mixins: [physicalProductMixin],
  props: {
    productInfo: {
      type: Object,
      default: null
    },
    productInfoManager: {
      type: Object,
      default: null
    }
  },
  emits: ['cancel', 'next', 'product-uploaded', 'valid'],
  data() {
    return {
      uploadUrl: `${apiBaseUrl}/lcp/upload`,
      maxFileSize: FILE_SIZE_LIMITS.PRODUCT_UPLOAD,
      isUploaded: false,
      hasError: false,
      selectedFile: null,
      uploadProgress: 0,
      uploadedProductInfo: null,
    };
  },
  computed: {
    acceptedExtensions() {
      return ['.epub', '.pdf', '.lpf'];
    },
    isEditing() {
      return !!this.productInfo;
    },
    shouldShowDetails() {
      return this.isEditing || this.uploadedProductInfo !== null;
    },
    displayBookId() {
      if (this.isEditing) {
        return this.productInfo.id || (this.productInfo.book && this.productInfo.book.id);
      }
      return this.uploadedProductInfo?.book?.id || null;
    },
    displayBookTitle() {
      if (this.isEditing) {
        return this.productInfo.title || (this.productInfo.book && this.productInfo.book.title);
      }
      return this.uploadedProductInfo?.book?.title || null;
    },
    displayEncryptedFilePath() {
      if (this.isEditing) {
        if (this.productInfo.encryptedFile) {
          return this.productInfo.encryptedFile.filePath;
        }
        if (this.productInfo.book && this.productInfo.book.encryptedFile) {
          return this.productInfo.book.encryptedFile.filePath;
        }
      }
      return this.uploadedProductInfo?.encryptedFile?.filePath || null;
    },
    displayUnencryptedFilePath() {
      if (this.isEditing) {
        if (this.productInfo.unencryptedFile) {
          return this.productInfo.unencryptedFile.filePath;
        }
        if (this.productInfo.book && this.productInfo.book.unencryptedFile) {
          return this.productInfo.book.unencryptedFile.filePath;
        }
      }
      return this.uploadedProductInfo?.unencryptedFile?.filePath || null;
    },
    displayFormat() {
      if (this.isEditing) {
        if (this.productInfo.format) {
          return this.productInfo.format._id || this.productInfo.format.mimeType || this.productInfo.format;
        }
        if (this.productInfo.book && this.productInfo.book.format) {
          return this.productInfo.book.format._id || this.productInfo.book.format.mimeType || this.productInfo.book.format;
        }
      }
      return this.uploadedProductInfo?.book?.format?._id || this.uploadedProductInfo?.book?.format?.mimeType || null;
    },
    uploadStatus() {
      if (!this.uploadedProductInfo) {
        return { 
          color: 'info',
          title: this.$t('admin.productsDatabase.importWizard.upload.status'),
          message: this.$t('admin.productsDatabase.importWizard.upload.noDataYet')
        };
      }
      
      if (this.isPhysicalProduct && this.uploadedProductInfo.book) {
        return {
          color: 'success',
          title: this.$t('admin.productsDatabase.importWizard.upload.success'),
          message: this.$t('admin.productsDatabase.importWizard.upload.physicalProductCreated')
        };
      }
      
      const encryption = this.uploadedProductInfo.encryption || {};
      
      if (encryption.status === 'success') {
        if (encryption.notificationError) {
          return {
            color: 'warning',
            title: this.$t('admin.productsDatabase.importWizard.upload.partialSuccess'),
            message: this.$t('admin.productsDatabase.importWizard.upload.encryptionSuccessNotificationFailed')
          };
        } else {
          return {
            color: 'success',
            title: this.$t('admin.productsDatabase.importWizard.upload.success'),
            message: this.$t('admin.productsDatabase.importWizard.upload.encryptionAndNotificationSuccess')
          };
        }
      } else {
        return {
          color: 'error',
          title: this.$t('admin.productsDatabase.importWizard.upload.failed'),
          message: this.$t('admin.productsDatabase.importWizard.upload.encryptionFailed')
        };
      }
    },
    hasDetailedOutput() {
      return this.uploadedProductInfo && 
        ((this.uploadedProductInfo.encryption && this.uploadedProductInfo.encryption.output) || 
         Object.keys(this.uploadedProductInfo).length > 0);
    },
    productDisplayData() {
      return {
        bookId: this.displayBookId,
        title: this.displayBookTitle,
        encryptedFilePath: this.displayEncryptedFilePath,
        unencryptedFilePath: this.displayUnencryptedFilePath,
        format: this.displayFormat,
        mediaObjectId: this.uploadedProductInfo?.mediaObject?.id,
        mediaObjectFilePath: this.uploadedProductInfo?.mediaObject?.filePath
      };
    },
    isValid() {
      if (this.isEditing) return true;
      
      if (this.isUploaded && this.uploadedProductInfo) return true;
      
      return false;
    }
  },
  watch: {
    isValid(newVal) {
      this.$emit('valid', newVal);
    }
  },
  mounted() {
    if (this.isEditing && this.productInfo && this.productInfoManager) {
      this.productInfoManager.setUploadedData(this.productInfo);
      
      // Detect and initialize physical product state
      const book = this.productInfo.book;
      if (book && book.format) {
        const formatId = typeof book.format === 'string'
          ? book.format.split('/').pop()
          : book.format._id || book.format.mimeType;
        if (isPrintFormat(book.format)) {
          this.isPhysicalProduct = true;
          this.physicalProductTitle = book.title;
          // Map legacy 'print' to 'paperback' for the selector, otherwise use the specific format
          this.bindingType = formatId === 'print' ? 'paperback' : formatId;
        }
      }
    }
    this.$emit('valid', this.isValid);
  },
  methods: {
    validateBookFile(file) {
      const fileName = file.name.toLowerCase();
      
      if (!this.acceptedExtensions.some(ext => fileName.endsWith(ext))) {
        return this.$t('admin.productsDatabase.importWizard.upload.errors.invalidFormat');
      }
      return true;
    },
    
    handleFileSelected(file) {
      this.selectedFile = file;
      this.isUploaded = false;
      this.hasError = false;
      this.uploadedProductInfo = null;
    },
    
    handleUploadProgress(progress) {
      this.uploadProgress = progress;
    },
    
    handleUploadSuccess(data) {
      this.isUploaded = true;
      this.uploadedProductInfo = data;
      
      // Check for partial success (encryption succeeded but LCP notification failed)
      if (data.encryption && data.encryption.status === 'success' && data.encryption.notificationError) {
        this.hasError = false; // Not treating notification error as a complete failure
        
        // Show warning notification
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('admin.productsDatabase.importWizard.upload.partialSuccessNotification'),
          color: 'warning'
        });
      } else {
        this.hasError = false;
      }
      
      if (this.productInfoManager) {
        this.productInfoManager.setUploadedData(data);
      }
    },
    
    handleUploadError(errorInfo) {
      this.hasError = true;
      console.error('Upload error:', errorInfo.error);
      
      // Store the error response if available
      if (errorInfo.error && errorInfo.error.response && errorInfo.error.response.data) {
        this.uploadedProductInfo = errorInfo.error.response.data;
      }
    },
    
    resetUpload() {
      this.isUploaded = false;
      this.hasError = false;
      this.selectedFile = null;
      this.uploadProgress = 0;
      this.uploadedProductInfo = null;
      this.resetPhysicalProduct();
    }
  }
};
</script>

<style scoped>
.upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-area-active {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.05);
}

.upload-area-error {
  border-color: #ff5252;
  background-color: rgba(255, 82, 82, 0.05);
}

.upload-area-success {
  border-color: #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
}

.output-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background: #f5f5f5;
  padding: 8px;
  font-size: 12px;
  font-family: monospace;
  max-height: 300px;
  overflow-y: auto;
}

.v-simple-table {
  background-color: transparent !important;
}

.v-simple-table td {
  padding: 4px 8px !important;
}
</style>
